import { CALORIE_BALANCE_RANGE_BOUNDS } from "@/features/overview/constants/calorie-balance";
import { cn } from "@/utils/cn";
import { textVariants } from "@/utils/text-variants";
import { IonSkeletonText } from "@ionic/react";
import { memo } from "react";
import type { TimeRangeKey } from "../time-range-buttons";

type PlanStatus = "on" | "nearly" | "adjust";

const getStatus = (calorieDeviation: number, min: number, max: number): PlanStatus => {
  if (calorieDeviation >= min && calorieDeviation <= max) {
    return "on";
  }
  if (calorieDeviation < min) {
    return "nearly";
  }
  return "adjust";
};

const getTitle = (status: PlanStatus) => {
  switch (status) {
    case "on":
      return "On plan";
    case "nearly":
      return "Nearly on plan";
    case "adjust":
      return "You're over your planned calories";
  }
};

const getDeviationMessage = (dailyDeviation: number): string => {
  const kcal = Math.abs(Math.round(dailyDeviation));
  const direction = dailyDeviation > 0 ? "over" : "under";
  return `${kcal} kcal / day ${direction}`;
};

function PlanCheckOutlineIcon() {
  return (
    <svg
      width="25"
      height="25"
      viewBox="0 0 25 25"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden
    >
      <path
        fillRule="evenodd"
        fill="var(--color-check-green)"
        d="M17.5383 8.71172C17.6255 8.79879 17.6946 8.90218 17.7418 9.01599C17.789 9.1298 17.8132 9.2518 17.8132 9.375C17.8132 9.4982 17.789 9.6202 17.7418 9.73401C17.6946 9.84782 17.6255 9.95121 17.5383 10.0383L10.9758 16.6008C10.8887 16.6879 10.7853 16.7571 10.6715 16.8043C10.5577 16.8515 10.4357 16.8757 10.3125 16.8757C10.1893 16.8757 10.0673 16.8515 9.9535 16.8043C9.83969 16.7571 9.73629 16.6879 9.64922 16.6008L6.83672 13.7883C6.66081 13.6124 6.56198 13.3738 6.56198 13.125C6.56198 12.8762 6.66081 12.6376 6.83672 12.4617C7.01264 12.2858 7.25123 12.187 7.5 12.187C7.74878 12.187 7.98737 12.2858 8.16329 12.4617L10.3125 14.6121L16.2117 8.71172C16.2988 8.62455 16.4022 8.5554 16.516 8.50822C16.6298 8.46105 16.7518 8.43676 16.875 8.43676C16.9982 8.43676 17.1202 8.46105 17.234 8.50822C17.3478 8.5554 17.4512 8.62455 17.5383 8.71172ZM24.375 12.1875C24.375 14.598 23.6602 16.9543 22.321 18.9585C20.9819 20.9627 19.0784 22.5248 16.8515 23.4473C14.6245 24.3697 12.174 24.6111 9.80984 24.1408C7.4457 23.6706 5.27409 22.5098 3.56964 20.8054C1.86519 19.1009 0.704442 16.9293 0.234185 14.5652C-0.236072 12.201 0.00528065 9.75052 0.927724 7.52354C1.85017 5.29657 3.41227 3.39314 5.41649 2.05396C7.42072 0.714784 9.77705 0 12.1875 0C15.4188 0.00341229 18.5167 1.28854 20.8016 3.5734C23.0865 5.85826 24.3716 8.95622 24.375 12.1875ZM22.5 12.1875C22.5 10.1479 21.8952 8.15406 20.762 6.45818C19.6289 4.7623 18.0183 3.44052 16.1339 2.65999C14.2496 1.87946 12.1761 1.67524 10.1756 2.07315C8.17521 2.47106 6.3377 3.45323 4.89547 4.89546C3.45324 6.33769 2.47107 8.1752 2.07316 10.1756C1.67525 12.1761 1.87947 14.2496 2.66 16.1339C3.44053 18.0183 4.7623 19.6289 6.45819 20.762C8.15407 21.8952 10.1479 22.5 12.1875 22.5C14.9216 22.4969 17.5428 21.4094 19.4761 19.4761C21.4094 17.5428 22.4969 14.9216 22.5 12.1875Z"
      />
    </svg>
  );
}

export const PlanStatusCircle = memo(function PlanStatusCircle({
  value,
  dailyValue,
  selectedRange,
  isLoading = false,
}: {
  value: number;
  dailyValue?: number;
  selectedRange: TimeRangeKey;
  isLoading?: boolean;
}) {
  const { min, max } = CALORIE_BALANCE_RANGE_BOUNDS[selectedRange];
  const status = getStatus(value, min, max);
  const title = getTitle(status);
  const message = getDeviationMessage(dailyValue ?? 0);
  const showCheck = status !== "adjust";

  if (isLoading) {
    return (
      <div className="flex flex-col items-center gap-[13px]">
        <IonSkeletonText animated className="h-[45px] w-40 rounded-xl" />
        <IonSkeletonText animated className="h-5 w-40 rounded-lg" />
        <IonSkeletonText animated className="h-6 w-6 rounded-full" />
      </div>
    );
  }

  return (
    <div className="relative flex flex-col items-center gap-[13px]">
      {/* Radial purple glow behind text, matching Figma "shadow blur" node */}
      <div
        aria-hidden
        className="pointer-events-none absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2"
        style={{
          width: 287,
          height: 287,
          opacity: 0.32,
          zIndex: "-1",
          filter: "blur(40.55px)",
          borderRadius: "50%",
          background: "var(--color-on-plan-glow)",
          boxShadow: "0 0 0 36px var(--color-on-plan-glow-ring)",
        }}
      />
      <p
        className="text-center text-white"
        style={{
          fontFamily: "var(--font-crimson-text)",
          fontWeight: 600,
          fontSize: "35px",
          lineHeight: "45px",
          letterSpacing: "-1px",
        }}
      >
        {title}
      </p>
      <p
        className={cn(textVariants.bodySmall, "text-center text-white")}
        style={{ lineHeight: "20px" }}
      >
        {message}
      </p>
      {showCheck && <PlanCheckOutlineIcon />}
    </div>
  );
});
